import React, { useState, useEffect, useCallback } from 'react';
import { getDestinoById, createDestino, updateDestino } from '../services/destinoCreditoService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, RefreshCw, Target, Check, AlertCircle, FileText } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const DestinoCreditoForm = () => {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);

    const [form, setForm] = useState({
        nombre: '',
        descripcion: ''
    });

    const fetchDestino = useCallback(async () => {
        if (!isEditMode) return;
        try {
            setFetching(true);
            const data = await getDestinoById(token, id);
            setForm({
                nombre: data.nombre,
                descripcion: data.descripcion || ''
            });
        } catch (error) {
            toast.error('Error al cargar datos del destino');
            navigate('/parametrizacion/destinos-fondos');
        } finally {
            setFetching(false);
        }
    }, [id, isEditMode, token, navigate]);

    useEffect(() => {
        fetchDestino();
    }, [fetchDestino]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.nombre.trim()) {
            toast.error('El nombre del destino es obligatorio');
            return;
        }

        try {
            setLoading(true);
            const dataToSend = {
                nombre: form.nombre.toUpperCase(),
                descripcion: form.descripcion
            };

            if (isEditMode) {
                await updateDestino(token, id, dataToSend);
                toast.success('Destino actualizado correctamente');
            } else {
                await createDestino(token, dataToSend);
                toast.success('Destino creado con éxito');
            }
            setTimeout(() => navigate('/parametrizacion/destinos-fondos'), 1000);
        } catch (error) {
            toast.error('Error al guardar los cambios');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 animate-in fade-in">
                <RefreshCw className="w-10 h-10 animate-spin text-blue-600" />
                <p className="text-slate-500 font-bold uppercase text-xs tracking-widest italic">Sincronizando con el servidor...</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-xl border border-slate-300 mb-10 overflow-hidden rounded-none animate-in fade-in duration-500">
            <Toaster position="top-right" />

            {/* Header Enterprise Estilo Oracle APEX - Réplica Tipos de Crédito */}
            <div className="bg-slate-700 p-4 flex items-center justify-between text-white shadow-inner border-b border-slate-800">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/parametrizacion/destinos-fondos')}
                        className="p-1.5 hover:bg-slate-600 transition-colors text-slate-300 hover:text-white"
                    >
                        <ArrowLeft className="w-6 h-6 shadow-sm" />
                    </button>
                    <div>
                        <h1 className="text-lg font-black tracking-tight uppercase italic">
                            {isEditMode ? 'Mantenimiento: ' + form.nombre : 'Definición de Nuevo Destino de Fondos'}
                        </h1>
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.3em]">Módulo de Configuración Operativa Maestra</p>
                    </div>
                </div>
                <div className="bg-slate-600 px-3 py-1 text-[10px] font-black uppercase border border-slate-500 shadow-sm">Mv-DEST-0.1</div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 bg-[#f8fafc]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

                    {/* BLOQUE IZQUIERDO: Definición del Destino */}
                    <div className="space-y-8">
                        <div className="bg-white border border-slate-200 p-6 relative overflow-hidden shadow-sm">
                            <div className="absolute top-0 left-0 w-1 h-full bg-slate-700"></div>
                            <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-2">
                                <FileText className="w-4 h-4 text-slate-400" />
                                <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Información Operativa del Destino</h3>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Nombre Identificador (Alcance) <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            required
                                            className="w-full pl-4 pr-10 py-3 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase bg-slate-50 focus:bg-white transition-all text-[13px] rounded-none shadow-sm"
                                            placeholder="EJ: REFORMA DE VIVIENDA, COMPRA DE VEHÍCULO"
                                            value={form.nombre}
                                            onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })}
                                        />
                                        <Target className="absolute right-3 top-3.5 w-4 h-4 text-slate-300" />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-tight">Alcance / Descripción Detallada</label>
                                    <textarea
                                        rows="5"
                                        className="w-full px-4 py-3 border border-slate-300 focus:border-blue-600 outline-none text-[12px] italic text-slate-600 bg-white transition-all border-dashed rounded-none shadow-sm resize-none"
                                        placeholder="Especifique el alcance de este destino de fondos para el análisis de riesgo..."
                                        value={form.descripcion}
                                        onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BLOQUE DERECHO: Guía y Ayuda (Mismo estilo que Config Contable) */}
                    <div className="space-y-6 bg-slate-200/50 p-6 border border-slate-300 shadow-inner">
                        <div className="text-center mb-4">
                            <h2 className="text-xs font-black text-slate-700 uppercase tracking-[0.2em]">Guía de Configuración</h2>
                            <div className="h-1 w-12 bg-blue-600 mx-auto mt-1"></div>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-slate-200 p-5 shadow-sm hover:border-blue-400 transition-all">
                                <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1 border-b border-slate-100 pb-1">
                                    <AlertCircle className="w-3.5 h-3.5 text-blue-500" />
                                    Uso en Solicitudes
                                </h3>
                                <p className="text-[11px] text-slate-600 font-bold uppercase leading-relaxed">
                                    Este campo aparecerá en el selector de la solicitud de crédito. Debe ser descriptivo para que el socio identifique rápidamente su motivo de préstamo.
                                </p>
                            </div>

                            <div className="bg-white border border-slate-200 p-5 shadow-sm hover:border-blue-400 transition-all">
                                <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1 border-b border-slate-100 pb-1">
                                    <Target className="w-3.5 h-3.5 text-emerald-500" />
                                    Impacto en Riesgos
                                </h3>
                                <p className="text-[11px] text-slate-600 font-bold uppercase leading-relaxed">
                                    Los destinos de fondos se utilizan para la segmentación de cartera y reportes a entes reguladores. Asegúrese de mantener una nomenclatura estandarizada.
                                </p>
                            </div>

                            <div className="bg-amber-50 border border-amber-200 p-4 shadow-sm flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                                <div className="text-[10px] text-amber-800 font-bold uppercase leading-relaxed">
                                    <p className="mb-1">⚠️ AVISO OPERATIVO:</p>
                                    Evite crear destinos duplicados. Si un destino ya no se utiliza, se recomienda editar su nombre para indicar que está inactivo (Ej: [INACTIVO] NOMBRE).
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer del Formulario - Réplica Exacta */}
                <div className="mt-12 pt-6 border-t border-slate-300 flex justify-between items-center">
                    <button
                        type="button"
                        onClick={() => navigate('/parametrizacion/destinos-fondos')}
                        className="px-8 py-2 border border-slate-300 text-slate-500 hover:bg-slate-200 font-black text-[11px] uppercase transition-all shadow-sm rounded-none"
                    >
                        Cancelar y Salir
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="px-12 py-3 bg-[#0066cc] text-white shadow-lg hover:bg-[#004d99] transition-all font-black text-[12px] uppercase tracking-[0.1em] flex items-center gap-2 border border-[#0052a3] shadow-[0_4px_10px_rgba(0,102,204,0.3)] disabled:opacity-50"
                    >
                        {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                        {isEditMode ? 'Consolidar Cambios' : 'Confirmar Definición'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default DestinoCreditoForm;
